import {call, fork, put, race, select, take, takeEvery} from "redux-saga/effects";
import {ActionType, getType} from "typesafe-actions";
import {consume, purchase} from "../actions";
import {IApplicationState} from "../../index";
import {trackError} from "../../log/actions";
import {LogLevel} from "../../log/types";
import {showAlert} from "../../dialog-manager/dialogs/showAlert";
import playSound from "../../../shared/sounds/playSound";
import {exportStateFlow} from "../../common/stateFlowEnhancer";
import apiCall from "../../../shared/utils/apiCall";
import consumeProduct from "./consumeProduct";
import forkWithErrHandler from "../../../shared/utils/forkWithErrHandler";
import i18n from "../../../shared/i18n";
import { IPurchase } from "../../../sdk/types";
import { SDK } from "../../../sdk";

export default function* purchaseWorker() {
    yield takeEvery(getType(purchase.request), function* ({ payload: productId }: ActionType<typeof purchase>) {
        const {isReady, appVersion, userId} = yield select(({ payments, player, settings }: IApplicationState) => ({
            isReady: payments.isReady,
            appVersion: settings.appVersion,
            userId: settings.userId || "_" + player.id,
        }));

        const registerError = function* (error: any, message?: string) {
            yield put(purchase.failure(productId));

            const logLevel = error?.code === "USER_INPUT"
                ? LogLevel.Warn
                : LogLevel.Fatal;

            yield put(trackError({
                logLevel: logLevel,
                category: "paymentsSaga",
                message: message || "Failed to purchase product",
                error: error instanceof Error ? error : undefined,
                data: {
                    productId,
                    error: error?.message || error,
                }
            }));

            if (logLevel === LogLevel.Fatal) {
                yield call(showAlert, i18n("inapppurchasesshop.purchasefailed"));
            }
        };

        if (!isReady) {
            yield registerError("Payments SDK is not ready yet");
            return;
        }

        let purchaseInfo: IPurchase | void;

        try {
            purchaseInfo = yield call([SDK.instance, SDK.instance.purchase], {
                productId,
                developerPayload: {
                    userId,
                }
            });

            playSound("item-purchase");

            yield put(purchase.success(productId));

        }
        catch (error) {
            yield registerError(error);
        }

        if (!purchaseInfo)
            return;

        yield forkWithErrHandler("paymentsSaga", "logSuccessfulPurchase", function* () {
            const flow = yield call(exportStateFlow);

            yield apiCall({
                params: {
                    section: "log",
                    method: "post",
                },
                data: {
                    messages: [{
                        logLevel: LogLevel.Info,
                        category: "paymentsSaga",
                        message: `User #${userId} purchased product`,
                        data: {
                            appVersion: appVersion,
                            userId: userId,
                            clientTime: new Date().getTime(),
                            state: flow,
                            purchaseInfo
                        }
                    }]
                }
            });
        });

        yield fork(consumeProduct, purchaseInfo);

        const {success} = yield race({
            success: take(consume.success),
            failure: take(consume.failure),
        });

        if (!success) {
            yield call(showAlert, i18n("inapppurchasesshop.purchasefailed"));
        }
    });
}